// Logto Account API helpers. These talk directly to the Logto tenant (not the
// OmniZen backend), so they use the *opaque* OP access token obtained via
// getAccessToken() with no resource argument — distinct from the resource
// token in client.ts/useApi.ts that targets VITE_LOGTO_API_RESOURCE.

const LOGTO_ENDPOINT = import.meta.env.VITE_LOGTO_ENDPOINT as string | undefined;

export interface MyAccount {
  id: string;
  // Logto exposes whether the user has a password credential. Used to decide
  // between the active "change password" action and the disabled social-only
  // state. Other fields exist but aren't needed here.
  hasPassword?: boolean;
}

/**
 * Fetch the current user's Logto account. Returns null if the endpoint isn't
 * configured or the request fails — callers treat null as "unknown" and fall
 * back to the safe default rather than blocking the UI.
 */
export async function fetchMyAccount(opaqueToken: string | undefined): Promise<MyAccount | null> {
  if (!LOGTO_ENDPOINT || !opaqueToken) return null;
  try {
    const res = await fetch(`${LOGTO_ENDPOINT}/api/my-account`, {
      headers: { Authorization: `Bearer ${opaqueToken}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as MyAccount;
  } catch {
    return null;
  }
}

/**
 * Build the URL of Logto's hosted change-password page, with a redirect back
 * to the page the user came from. Logto sends them back to `returnTo` after a
 * successful change (and appends ?show_success=password).
 */
export function changePasswordUrl(returnTo: string): string {
  const base = LOGTO_ENDPOINT ?? '';
  return `${base}/account/password?redirect=${encodeURIComponent(returnTo)}`;
}
